import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  getOrders,
  createOrder,
  getProducts,
  getCustomers,
  Product,
  Customer,
  getNextInvoiceNumber
} from '@/services/firestore'
import { friendlyError } from '@/utils/friendlyError'
import { FilePlus, ShoppingCart, Trash2, RotateCcw, Save, Search, Scan } from 'lucide-react'

export const Route = createFileRoute('/admin/sales-entry')({
  component: SalesEntryPage,
})

interface EntryItem {
  id: string; // unique ID for list
  productId: string;
  name: string;
  code: string;
  hsCode?: string;
  price: number;
  quantity: number;
  uom: string;
  taxRate: number;
}

function SalesEntryPage() {
  const { user } = useAuth()
  
  // Data
  const [products, setProducts] = useState<Product[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loadingInitial, setLoadingInitial] = useState(true)

  // Form State
  const [cart, setCart] = useState<EntryItem[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [customerSearch, setCustomerSearch] = useState('')
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)
  
  const [productSearch, setProductSearch] = useState('')
  const [showProductDropdown, setShowProductDropdown] = useState(false)

  // Meta
  const [date, setDate] = useState(new Date().toISOString().slice(0, 16))
  const [docNumber, setDocNumber] = useState('')
  const [notes, setNotes] = useState('')
  const [discount, setDiscount] = useState(0)

  // Progress UI
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false)

  useEffect(() => {
    Promise.all([getProducts(), getCustomers()])
      .then(([p, c]) => {
        setProducts(p)
        setCustomers(c)
      })
      .finally(() => setLoadingInitial(false))
  }, [])

  // Load Previous Entry
  const loadPreviousEntry = async () => {
    setIsLoadingPrevious(true)
    try {
      const pastOrders = await getOrders({ status: 'completed' })
      if (!pastOrders || pastOrders.length === 0) {
        alert('No previous completed records found.')
        return
      }

      // getOrders is already ordered by createdAt desc, so [0] is the most recent
      const lastOrder = pastOrders[0]

      // Populate Cart
      const mappedItems: EntryItem[] = lastOrder.items.map(it => ({
        id: crypto.randomUUID(),
        productId: it.productId,
        name: it.name,
        code: it.code,
        hsCode: it.hsCode,
        price: it.unitPrice,
        quantity: it.quantity,
        uom: (it as any).uom || 'PCS',
        taxRate: it.taxRate || 0,
      }))

      setCart(mappedItems)

      // Populate Customer
      if (lastOrder.customerId && lastOrder.customerId !== 'walk-in') {
        const c = customers.find(x => x.id === lastOrder.customerId)
        if (c) setSelectedCustomer(c)
        else setCustomerSearch(lastOrder.customerName || '')
      } else {
        setSelectedCustomer(null)
      }

      setDiscount(lastOrder.discountAmount || 0)
      setNotes(lastOrder.notes || '')
      // Date is kept as current to indicate it's a new entry, unless user wants old date
    } catch (err) {
      alert(friendlyError(err, 'Could not fetch previous record'))
    } finally {
      setIsLoadingPrevious(false)
    }
  }

  // Cart operations
  const addProductToCart = (prod: Product) => {
    setCart(prev => {
      const existing = prev.find(i => i.productId === prod.id)
      if (existing) {
        return prev.map(i => i.productId === prod.id ? { ...i, quantity: i.quantity + 1 } : i)
      }
      return [...prev, {
        id: crypto.randomUUID(),
        productId: prod.id!,
        name: prod.name,
        code: prod.code,
        hsCode: prod.hsCode,
        price: prod.price,
        quantity: 1,
        uom: prod.unit || 'PCS',
        taxRate: prod.taxRate || 0,
      }]
    })
    setProductSearch('')
    setShowProductDropdown(false)
  }

  const updateCartItem = (id: string, field: keyof EntryItem, value: any) => {
    setCart(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item))
  }

  const removeCartItem = (id: string) => {
    setCart(prev => prev.filter(i => i.id !== id))
  }

  // Calculations
  const subtotal = cart.reduce((acc, i) => acc + (i.price * i.quantity), 0)
  const taxAmount = cart.reduce((acc, i) => acc + Math.round((i.price * i.quantity) * (i.taxRate / 100)), 0)
  const totalAmount = subtotal + taxAmount - discount

  // Submit
  const handleSubmit = async () => {
    if (cart.length === 0) {
      alert('Cannot submit an empty sales record.')
      return
    }

    setIsSubmitting(true)
    try {
      const finalOrderNumber = await getNextInvoiceNumber()

      // Convert local datetime to Date object
      const targetDate = new Date(date)
      const dateStr = date.split('T')[0]
      
      await createOrder({
        orderNumber: finalOrderNumber,
        invoiceNumber: finalOrderNumber,
        invoiceType: 'general',
        invoiceDate: dateStr,
        date: dateStr,
        docNumber,
        customerId: selectedCustomer?.id || 'walk-in',
        customerName: selectedCustomer?.name || customerSearch || 'Walk-in Customer',
        items: cart.map(i => ({
          productId: i.productId,
          name: i.name,
          code: i.code,
          hsCode: i.hsCode,
          quantity: i.quantity,
          unitPrice: i.price,
          total: i.price * i.quantity,
          taxRate: i.taxRate,
          taxAmount: Math.round(i.price * i.quantity * (i.taxRate / 100))
        })),
        subtotal,
        taxRate: 0,
        taxAmount,
        discountAmount: discount,
        totalAmount,
        paymentMethod: 'cash', // Defaulting to cash for manual entries, though we could make this configurable
        status: 'completed',
        notes,
        createdBy: user?.uid || '',
        // Override created date
        createdAt: targetDate, 
        completedAt: targetDate,
      } as any)

      alert('Sales entry recorded successfully!')
      
      // Clear form
      setCart([])
      setSelectedCustomer(null)
      setCustomerSearch('')
      setDocNumber('')
      setNotes('')
      setDiscount(0)
      setDate(new Date().toISOString().slice(0, 16))

    } catch (err) {
      alert(friendlyError(err, 'Failed to save sales record'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const filteredProducts = products.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()) || p.code.toLowerCase().includes(productSearch.toLowerCase())).slice(0, 10)
  const filteredCustomers = customers.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase()) || (c.phone && c.phone.includes(customerSearch))).slice(0, 5)

  if (loadingInitial) {
    return <div className="p-8 text-center text-slate-500">Loading modules...</div>
  }

  return (
    <div className="p-4 lg:p-8 bg-[#f0f5ff] min-h-screen">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-800 flex items-center gap-3">
            <FilePlus className="w-7 h-7 text-blue-600" />
            Manual Sales Entry
          </h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">Record past sales directly into the system database.</p>
        </div>
        <button 
          onClick={loadPreviousEntry}
          disabled={isLoadingPrevious}
          className="bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-all flex items-center gap-2 hover:shadow-md hover:border-blue-300 disabled:opacity-50"
        >
          {isLoadingPrevious ? <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"/> : <RotateCcw className="w-4 h-4" />}
          Load Previous Record
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* LEFT COLUMN: Data Input */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          
          {/* Metadata Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 p-6">
            <h2 className="text-[13px] font-bold tracking-widest text-slate-400 uppercase mb-4 mb-4">Invoice Information</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1.5">Date & Time</label>
                <input 
                  type="datetime-local" 
                  value={date} 
                  onChange={e => setDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-slate-800" 
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1.5">Document Number (Optional)</label>
                <input 
                  type="text" 
                  value={docNumber} 
                  onChange={e => setDocNumber(e.target.value)}
                  placeholder="e.g. DOC-9981"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-slate-800" 
                />
              </div>

              <div className="sm:col-span-2 relative">
                <label className="text-xs font-bold text-slate-600 block mb-1.5">Customer</label>
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input 
                    type="text" 
                    value={selectedCustomer ? selectedCustomer.name : customerSearch} 
                    onChange={e => {
                      if (selectedCustomer) setSelectedCustomer(null)
                      setCustomerSearch(e.target.value)
                      setShowCustomerDropdown(true)
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    placeholder="Search customer or leave blank for Walk-in"
                    className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-slate-800 bg-slate-50/50" 
                  />
                  {selectedCustomer && (
                    <button onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {showCustomerDropdown && customerSearch && !selectedCustomer && (
                  <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                    {filteredCustomers.length > 0 ? filteredCustomers.map(c => (
                      <button 
                        key={c.id} 
                        onClick={() => { setSelectedCustomer(c); setShowCustomerDropdown(false); setCustomerSearch(''); }}
                        className="w-full text-left px-4 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                      >
                        <div className="font-bold text-slate-800 text-sm">{c.name}</div>
                        {c.phone && <div className="text-[10px] text-slate-500">{c.phone}</div>}
                      </button>
                    )) : (
                      <div className="px-4 py-3 text-sm text-slate-500">No matching customers found. Will record as walk-in: "{customerSearch}"</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Items Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 flex flex-col flex-1 overflow-hidden min-h-[400px]">
            <div className="p-5 border-b border-slate-100">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-[13px] font-bold tracking-widest text-slate-400 uppercase">Items</h2>
              </div>

              <div className="relative z-10">
                <div className="relative">
                  <Scan className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => {
                      setProductSearch(e.target.value)
                      setShowProductDropdown(true)
                    }}
                    onFocus={() => setShowProductDropdown(true)}
                    placeholder="Search product code or name..."
                    className="w-full pl-9 pr-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all placeholder:font-normal"
                  />
                </div>

                {showProductDropdown && productSearch && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden max-h-60 overflow-y-auto">
                    {filteredProducts.length > 0 ? (
                      filteredProducts.map(p => (
                        <button
                          key={p.id}
                          onClick={() => addProductToCart(p)}
                          className="w-full flex items-center justify-between p-3 hover:bg-blue-50 border-b border-slate-50 transition-colors last:border-0"
                        >
                          <div className="text-left">
                            <div className="font-bold text-slate-800 text-sm">{p.name}</div>
                            <div className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded inline-block mt-1 font-mono">{p.code}</div>
                          </div>
                          <div className="font-black text-blue-600 text-sm">Rs {p.price.toLocaleString()}</div>
                        </button>
                      ))
                    ) : (
                      <div className="p-4 text-center text-slate-500 text-sm font-medium">No products found</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-auto bg-slate-50/50 p-5">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-400">
                  <ShoppingCart className="w-12 h-12 mb-3 stroke-1 text-slate-300" />
                  <p className="font-medium text-sm">No items added to the record.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cart.map((item, idx) => (
                    <div key={item.id} className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col sm:flex-row sm:items-center gap-4 group">
                      <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <input
                          value={item.name}
                          onChange={(e) => updateCartItem(item.id, 'name', e.target.value)}
                          className="font-bold text-slate-800 text-sm bg-transparent border-none p-0 focus:ring-0 w-full mb-1 truncate"
                        />
                        <div className="flex text-xs text-slate-500 gap-3">
                          <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{item.code}</span>
                          <span>UOM: <input className="w-12 bg-transparent text-slate-700 font-bold border-b border-slate-200 focus:border-blue-500 outline-none" value={item.uom} onChange={e => updateCartItem(item.id, 'uom', e.target.value)}/></span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 shrink-0 bg-slate-50 p-2 rounded-lg border border-slate-100">
                        <div className="flex flex-col items-center">
                          <label className="text-[9px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">Price</label>
                          <input type="number" value={item.price} onChange={e => updateCartItem(item.id, 'price', Number(e.target.value))} className="w-20 text-center font-bold text-sm bg-white border border-slate-200 rounded px-1 py-1 focus:outline-none focus:border-blue-500" />
                        </div>
                        <div className="text-slate-300 text-lg font-light">×</div>
                        <div className="flex flex-col items-center">
                          <label className="text-[9px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">Qty</label>
                          <input type="number" value={item.quantity} onChange={e => updateCartItem(item.id, 'quantity', Number(e.target.value))} className="w-16 text-center font-bold text-sm bg-white border border-slate-200 rounded px-1 py-1 focus:outline-none focus:border-blue-500" />
                        </div>
                        <div className="text-slate-300 text-lg font-light">=</div>
                        <div className="flex flex-col items-end w-24">
                          <label className="text-[9px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">Total</label>
                          <div className="font-black text-blue-600">{(item.price * item.quantity).toLocaleString()}</div>
                        </div>
                      </div>

                      <button onClick={() => removeCartItem(item.id)} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Summary & Action */}
        <div className="flex flex-col gap-6">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-xl text-white overflow-hidden">
            <div className="p-6">
              <h2 className="text-[13px] font-bold tracking-widest text-slate-400 uppercase mb-6 flex items-center gap-2">
                <ShoppingCart className="w-4 h-4" /> Summary
              </h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center text-slate-300 text-sm">
                  <span>Subtotal</span>
                  <span className="font-mono">Rs {subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center text-slate-300 text-sm">
                  <span>Sales Tax</span>
                  <span className="font-mono">Rs {taxAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center text-slate-300 text-sm">
                  <span>Discount</span>
                  <div className="flex items-center gap-1">
                    <span className="text-red-400">- Rs</span>
                    <input 
                      type="number" 
                      value={discount || ''} 
                      onChange={e => setDiscount(Number(e.target.value))}
                      className="bg-slate-800/50 border border-slate-600 rounded px-2 py-1 w-20 text-right font-mono focus:outline-none focus:border-blue-400 text-white"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-black/20 p-6 pt-5">
              <div className="flex justify-between items-end mb-6">
                <span className="text-slate-400 font-bold uppercase tracking-wider text-xs">Total Amount</span>
                <span className="text-3xl font-black text-white tracking-tight">Rs {totalAmount.toLocaleString()}</span>
              </div>

              <div className="mb-6">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">Remarks / Notes</label>
                <textarea 
                  value={notes} 
                  onChange={e => setNotes(e.target.value)}
                  className="w-full bg-slate-800/50 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500 placeholder-slate-500 font-medium min-h-[80px]"
                  placeholder="Additional context for this sales entry..."
                />
              </div>

              <button 
                onClick={handleSubmit}
                disabled={isSubmitting || cart.length === 0}
                className="w-full bg-blue-500 hover:bg-blue-400 text-white py-4 rounded-xl font-black text-[15px] shadow-lg shadow-blue-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:shadow-none hover:-translate-y-0.5 active:translate-y-0"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving Record...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Record Sales Entry
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
